/*
 * Copyright (C) 2022 Hal Perkins.  All rights reserved.  Permission is
 * hereby granted to students registered for University of Washington
 * CSE 331 for use solely during Winter Quarter 2022 for purposes of
 * the course.  No other use, copying, distribution, or modification
 * is permitted without prior written consent. Copyrights for
 * third-party components of this work must be honored.  Instructors
 * interested in reusing these course materials should contact the
 * author.
 */

import L, {latLngBounds, LatLngExpression} from "leaflet";
import React, {Component} from "react";
import {MapContainer, Marker, Popup, TileLayer, useMap, ZoomControl} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    UW_LATITUDE_CENTER,
    UW_LONGITUDE_CENTER,
} from "./Constants";
import "./App.css";
import { xToLon, yToLat } from "./Utils";

import 'leaflet/dist/leaflet.css';

// @ts-ignore
import icon from 'leaflet/dist/images/marker-icon.png';
// @ts-ignore
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// This defines the location of the map. These are the coordinates of the UW Seattle campus
const position: LatLngExpression = [UW_LATITUDE_CENTER, UW_LONGITUDE_CENTER];

const defaultIcon = new L.Icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

// NOTE: This component is a suggestion for you to use, if you would like to. If
// you don't want to use this component, you're free to delete it or replace it
// with your hw-lines MapComponent

interface MapComponentProps {
    route: Edge[];
    start: string;
    end: string;
    shortToLongNames: Map<string, string>;
    showAllBuildings: boolean;
    buildingPoints: Map<string, Coordinate>;
}

interface MapComponentState {
    route: [];
    buildingPoints: Map<string, Coordinate>; // A Map linking the (short) names of the buildings to their respective coordinates
    start: string;
    end: string;
    startMarker: any[];
    endMarker: any[];
    allMarkers: any[];
}

interface Edge {
    start: Coordinate;
    end: Coordinate;
    cost: number;
}

interface Coordinate {
    x: number;
    y: number;
}

class Coordinate implements Coordinate {
    x: number;
    y: number;
    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }
}

class MapComponent extends Component<MapComponentProps, MapComponentState> {
    constructor(props: MapComponentProps) {
        super(props);
        this.state = {
            route: [],
            buildingPoints: new Map<string, Coordinate>(),
            start: this.props.start,
            end: this.props.end,
            startMarker: [],
            endMarker: [],
            allMarkers: [],
        }
    }

    componentDidUpdate(
        prevProps: Readonly<MapComponentProps>,
        prevState: Readonly<MapComponentState>
    ) {
        // Update start marker when either the start building or buildingPoints prop changes.
        if (this.props.start !== prevProps.start || this.props.buildingPoints !== prevProps.buildingPoints) {
        const startCoordinate = this.props.buildingPoints.get(this.props.start);
        if (startCoordinate !== undefined) {
            this.setState({
            startMarker: [
                {
                name: this.props.shortToLongNames.get(this.props.start),
                y: yToLat(startCoordinate.y),
                x: xToLon(startCoordinate.x),
                },
            ],
            });
        } else {
            this.setState({ startMarker: [] });
        }
        }
        // Update end marker when either the end building or buildingPoints prop changes.
        if (this.props.end !== prevProps.end || this.props.buildingPoints !== prevProps.buildingPoints) {
        const endCoordinate = this.props.buildingPoints.get(this.props.end);
        if (endCoordinate !== undefined) {
            this.setState({
            endMarker: [
                {
                name: this.props.shortToLongNames.get(this.props.end),
                y: yToLat(endCoordinate.y),
                x: xToLon(endCoordinate.x),
                },
            ],
            });
        } else {
            this.setState({ endMarker: [] });
        }
        }
    }

    render() {
        const ChangeView = () => {
            const map = useMap();

            // Create a new LatLngBounds object
            let markerBounds = latLngBounds([]);

            // Extend bounds with start markers
            if (this.state.startMarker && this.state.startMarker.length > 0) {
                this.state.startMarker.forEach((marker: any) => {
                    markerBounds.extend([marker.y, marker.x]);
                });
            }

            // Extend bounds with end markers
            if (this.state.endMarker && this.state.endMarker.length > 0) {
                this.state.endMarker.forEach((marker: any) => {
                    markerBounds.extend([marker.y, marker.x]);
                });
            }

            if (this.state.allMarkers && this.state.allMarkers.length > 0) {
                this.state.allMarkers.forEach((marker: any) => {
                    markerBounds.extend([marker.y, marker.x]);
                });
            }

            // Check if markerBounds is valid before flying to bounds
            if (markerBounds.isValid()) {
                map.flyToBounds(markerBounds, {
                    padding: L.point(20, 20),
                    animate: true,
                });
            } else {
                // Optionally, set the map view to a default position
                map.setView({ lng: UW_LONGITUDE_CENTER, lat: UW_LATITUDE_CENTER }, 15);
            }

            return null;
        };

        return (
            <div id="map-container">
                <MapContainer
                    center={position}
                    zoomAnimation={true}
                    scrollWheelZoom={false}
                    zoomControl={false}
                    zoom={15}>
                    
                    <ZoomControl/>
                    <>
                    <TileLayer attribution='<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>'
                                url="https://api.maptiler.com/maps/bright/{z}/{x}/{y}.png?key=DrDEQfPqMHgssWalo3ve"/>
                    {this.props.route}
                    {this.state.startMarker.length > 0 &&
                        <Marker key={this.state.start}
                                position={[this.state.startMarker[0].y, this.state.startMarker[0].x]}>
                                <Popup>{this.state.startMarker[0].name}</Popup>
                        </Marker>
                    }
                    {/* End Marker */}
                    {this.state.endMarker.length > 0 &&
                        <Marker key={this.state.end}
                                position={[this.state.endMarker[0].y, this.state.endMarker[0].x]}>
                                <Popup>{this.state.endMarker[0].name}</Popup>
                        </Marker>
                    }
                    {/* All Building Markers */}
                    {this.props.showAllBuildings && Array.from(this.props.buildingPoints).map(([shortName, coord], idx) => (
                        <Marker 
                            key={shortName + idx} 
                            position={[yToLat(coord.y), xToLon(coord.x)]}
                        >
                            <Popup>{this.props.shortToLongNames.get(shortName)}</Popup>
                        </Marker>
                    ))}
                    <ChangeView/>
                    </>
                </MapContainer>
                
            </div>
        );
    }
}

export default MapComponent;
